<template>
  <div :style="{ 'max-width': inputMaxWidth ? `${inputMaxWidth}px` : '368px' }">
    <v-date-input
      name="date-range-picker"
      :density="dense ? 'compact' : 'default'"
      :prepend-icon="showIcon ? 'mdi-calendar' : ''"
      v-model="dates"
      variant="outlined"
      @click:clear="setDates([])"
      :suffix="rangeDaysSelectedText"
      :class="smallDayCount ? 'small-day-count' : ''"
      hide-details="auto"
      :clearable="clearable"
      :disabled="disabled"
      :label="translatedLabel"
      :error="error"
      :max-width="inputMaxWidth ?? 368"
      :min-width="inputMinWidth ?? '13rem'"
      :min="minDate"
      :max="maxDate"
      :cancel-text="$t('global.buttons.nevermind')"
      multiple="range" />
    <v-btn-toggle
      v-model="currentDefinedRange"
      density="compact"
      divided
      variant="outlined"
      v-if="useDefinedRanges"
      class="mt-1 rounded-0 full-width-button-group">
      <v-tooltip location="bottom" v-for="definedRange in definedRanges" :key="definedRange.text">
        <template v-slot:activator="{ props }">
          <v-btn
            :value="definedRange.value"
            v-bind="props"
            @click.stop="handleRangeBtnClick(definedRange)">
            {{ definedRange.text }}
          </v-btn>
        </template>
        <span>{{ definedRange.description }}</span>
      </v-tooltip>
    </v-btn-toggle>
  </div>
  <!-- This is still in labs, but it's amazing.  I would suggest pinning Vuetify to current used version
  until this is officially completed -->
</template>

<script>
import { DateTime } from 'luxon';
import { VDateInput } from 'vuetify/lib/labs/VDateInput/VDateInput.mjs';

export default {
  components: { VDateInput },
  props: {
    initialRange: {
      type: Array,
      required: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    inputMaxWidth: {
      type: String,
      required: false
    },
    inputMinWidth: {
      type: String,
      required: false
    },
    label: {
      type: String,
      required: false
    },
    showIcon: {
      type: Boolean,
      required: false,
      default: false
    },
    dense: {
      type: Boolean,
      required: false,
      default: true
    },
    height: {
      type: String,
      required: false,
      default: '20px'
    },
    outlined: {
      type: Boolean,
      required: false,
      default: false
    },
    initialDefinedRange: {
      type: String,
      required: false,
      default: null
    },
    useDefinedRanges: {
      type: Boolean,
      required: false,
      default: false
    },
    clearable: {
      type: Boolean,
      required: false,
      default: false
    },
    maxDateRangeDays: {
      type: Number,
      required: false
    },
    error: [Boolean, Object],
    showDayCount: {
      type: Boolean,
      required: false,
      default: true
    },
    smallDayCount: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      dates: [],
      dateMenu: false,
      currentDefinedRange: this.initialDefinedRange,
      definedRanges: [],
      originalRange: [],
      minDate: new Date('2010-01-01'),
      DateTime,
      isDefinedRangeSelected: false
    };
  },
  computed: {
    translatedLabel() {
      return this.label ?? this.$t(`forms.elements.selects.dateRange.labels.dateRange`);
    },
    maxDate() {
      let maxDate = null;
      if (this.maxDateRangeDays > 0 && this.dates[0]) {
        maxDate = DateTime.fromISO(this.dates[0]).plus({ days: this.maxDateRangeDays }).toJSDate();
      }
      return maxDate;
    },
    rangeDaysSelected() {
      if (this.dates?.length) {
        const start = DateTime.fromJSDate(this.dates[0]);
        const end = DateTime.fromJSDate(this.dates[this.dates.length - 1] ?? start.toJSDate());
        const diff = end.diff(start, 'days').toObject();
        return Math.floor(diff.days + 1);
      }

      return 0;
    },
    rangeDaysSelectedText() {
      if (!this.showDayCount) {
        return null;
      }
      if (this.rangeDaysSelected > 1) {
        return `${this.rangeDaysSelected} days selected`;
      } else if (this.rangeDaysSelected >= 0) {
        return '1 day selected';
      }

      return null;
    }
  },
  methods: {
    formatDate(date) {
      return DateTime.fromJSDate(date).toFormat(this.novaCore.LuxonDateTimeFormats.DateSlashed);
    },
    cancel() {
      this.dates = this.originalRange ?? this.initialRange;
      this.saveDates(this.dates);
    },
    saveDates(dates) {
      if (!this.isDefinedRangeSelected) {
        this.currentDefinedRange = null;
      }
      const firstAndLastDates = [dates[0], dates[dates.length - 1]];
      if (this.useDefinedRanges) {
        this.$emit('update:model-value', {
          range: firstAndLastDates,
          definedRange: this.currentDefinedRange
        });
      } else {
        this.$emit('update:model-value', firstAndLastDates);
      }
    },
    setDatesForDefinedRange() {
      this.definedRanges.forEach(range => {
        if (range.value === this.currentDefinedRange) {
          this.dates = [momentjs().startOf(range.startOf), momentjs()].map(d => d.toDate());
          this.saveDates(this.dates);
        }
      });
    },
    // Immutable, do not change, other comps rely on this method
    setCurrentDefinedRange(range) {
      this.isDefinedRangeSelected = true;
      this.currentDefinedRange = range;
    },
    // Immutable, do not change, other comps rely on this method
    setDates(dates) {
      this.dates = dates;
      this.saveDates(dates);
    },
    setDefinedRangeOptions() {
      this.definedRanges = Object.keys(this.novaCore.ReportSearchDefinedRanges).map(range => {
        const rangeVal = this.novaCore.ReportSearchDefinedRanges[range];
        const metadata = this.novaCore.definedRangeMetadata(rangeVal);
        return {
          text: range,
          value: rangeVal,
          description: metadata.description,
          startOf: metadata.startOf
        };
      });
    },
    handleRangeBtnClick(definedRange) {
      this.currentDefinedRange = definedRange.value;
      this.isDefinedRangeSelected = true;
    }
  },
  watch: {
    currentDefinedRange() {
      this.setDatesForDefinedRange();
    },
    dates(newVal) {
      if (newVal?.length) {
        this.setDates(newVal);
        this.isDefinedRangeSelected = false;
      }
    }
  },
  mounted() {
    if (this.useDefinedRanges) {
      this.setDefinedRangeOptions();
    }
    if (this.initialRange) {
      this.setDates([...this.initialRange]);
    } else if (this.initialDefinedRange) {
      this.setDatesForDefinedRange();
    } else {
      this.dates = [];
    }
  }
};
</script>

<style lang="scss">
.small-day-count .v-text-field__suffix {
  min-height: 0;
  height: auto;
  padding: 0;
  position: absolute;
  right: 5px;
  bottom: 0;
  font-size: 13px;
}
</style>
